import React, { useCallback, useMemo, useRef } from 'react';
import { formatMoney, signClass } from '../utils/assetHoldingCalc';
import './AssetSimpleTrendChart.css';

function formatAxisDate(dateStr) {
  const s = String(dateStr || '').slice(0, 10);
  return s.length >= 10 ? s.slice(5) : s;
}

function pickAxisLabelIndices(length) {
  if (length <= 0) return [];
  if (length === 1) return [0];
  if (length === 2) return [0, length - 1];
  const mid = Math.floor(length / 2);
  return Array.from(new Set([0, mid, length - 1])).sort((a, b) => a - b);
}

/** 生成约 tickCount 个可读刻度（含两端） */
function buildNiceYTicks(yMin, yMax, tickCount = 3) {
  const lo = Number(yMin);
  const hi = Number(yMax);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  if (hi === lo) return [lo];
  const n = Math.max(2, Math.min(5, tickCount));
  const ticks = [];
  for (let i = 0; i < n; i += 1) {
    ticks.push(lo + ((hi - lo) * i) / (n - 1));
  }
  return ticks;
}

function compactAxisNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toFixed(0);
  if (abs >= 100) return n.toFixed(1);
  if (abs >= 1) return n.toFixed(2);
  if (abs >= 0.01) return n.toFixed(2);
  if (abs === 0) return '0';
  return n.toFixed(3);
}

export default function AssetSimpleTrendChart({
  points,
  hideMoney = false,
  height = 180,
  valueFormatter,
  axisValueFormatter,
  activeIndex,
  onActiveIndexChange,
  showLegend = true,
  showYAxis = false,
  preserveAspect = 'none',
  legendPosition,
  chartId = 'assetTrend'
}) {
  const svgRef = useRef(null);
  const useMeetLayout = preserveAspect === 'meet';
  const legendOnTop = legendPosition
    ? legendPosition === 'top'
    : useMeetLayout;
  const fillGradientId = `${chartId}Fill`;

  const chart = useMemo(() => {
    const source = (points || []).filter((p) => p && Number.isFinite(Number(p.value)));
    if (!source.length) return null;
    let data = source.slice();
    if (data.length === 1) {
      data = [data[0], { ...data[0] }];
    }
    const values = data.map((p) => Number(p.value));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = (max - min) * 0.08 || (Math.abs(max) > 0 ? Math.abs(max) * 0.05 : 1);
    const yMin = min - pad;
    const yMax = max + pad;
    const width = 640;
    const h = height;
    const left = showYAxis ? 52 : 8;
    const right = width - 8;
    const top = 12;
    const bottom = h - 8;
    const coords = data.map((p, i) => {
      const x = left + (i / (data.length - 1)) * (right - left);
      const y = bottom - ((Number(p.value) - yMin) / (yMax - yMin)) * (bottom - top);
      return { x, y, index: i, ...p };
    });
    const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ');
    const area = `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${bottom} L ${coords[0].x.toFixed(1)} ${bottom} Z`;
    const lastIndex = coords.length - 1;
    const resolvedIndex = Number.isFinite(Number(activeIndex))
      ? Math.max(0, Math.min(lastIndex, Number(activeIndex)))
      : lastIndex;
    const active = coords[resolvedIndex] || coords[lastIndex];
    const axisLabels = pickAxisLabelIndices(coords.length).map((idx) => ({
      idx,
      text: formatAxisDate(coords[idx].date),
      x: coords[idx].x
    }));
    const yTickValues = showYAxis ? buildNiceYTicks(yMin, yMax, 3) : [];
    const yTicks = yTickValues.map((value) => ({
      value,
      y: bottom - ((value - yMin) / (yMax - yMin)) * (bottom - top)
    }));
    // 零轴（若落在可视范围内）
    const zeroInRange = showYAxis && yMin < 0 && yMax > 0;
    const zeroY = zeroInRange
      ? bottom - ((0 - yMin) / (yMax - yMin)) * (bottom - top)
      : null;
    return {
      coords,
      line,
      area,
      active,
      lastIndex,
      axisLabels,
      yTicks,
      zeroY,
      sourceCount: source.length,
      width,
      h,
      left,
      right,
      top,
      bottom
    };
  }, [points, height, activeIndex, showYAxis]);

  const formatValue = (value) => {
    if (hideMoney) return '****';
    if (typeof valueFormatter === 'function') return valueFormatter(value);
    return formatMoney(value);
  };

  const formatAxisValue = (value) => {
    if (hideMoney) return '****';
    if (typeof axisValueFormatter === 'function') return axisValueFormatter(value);
    if (typeof valueFormatter === 'function') {
      const raw = String(valueFormatter(value) || '');
      // 纵轴空间紧，去掉首尾空格；过长则回退紧凑数字
      if (raw.length <= 8) return raw;
    }
    return compactAxisNumber(value);
  };

  const pickIndexFromClientX = useCallback((clientX) => {
    if (!chart || !svgRef.current || typeof onActiveIndexChange !== 'function') return;
    const rect = svgRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const svgX = ((clientX - rect.left) / rect.width) * chart.width;
    let nearest = 0;
    let minDist = Infinity;
    chart.coords.forEach((c, i) => {
      const dist = Math.abs(c.x - svgX);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    onActiveIndexChange(nearest <= 0 ? 0 : Math.min(nearest, Math.max(0, chart.sourceCount - 1)));
  }, [chart, onActiveIndexChange]);

  const handlePointer = (event) => {
    const clientX = event.clientX ?? event.changedTouches?.[0]?.clientX;
    if (clientX == null) return;
    pickIndexFromClientX(clientX);
  };

  if (!chart) {
    return <div className="empty-hint">数据不足，无法绘制趋势</div>;
  }

  const activePoint = chart.active;
  const xAxisPadLeft = showYAxis ? `${(chart.left / chart.width) * 100}%` : '1.25%';
  const xAxisPadRight = '1.25%';

  const legendNode = showLegend ? (
    <div className={`asset-trend-chart-legend${legendOnTop ? ' asset-trend-chart-legend--top' : ''}`}>
      <span>{activePoint.date}</span>
      <span className={signClass(activePoint.value)}>
        {formatValue(activePoint.value)}
      </span>
    </div>
  ) : null;

  const chartBody = (
    <>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${chart.width} ${chart.h}`}
        preserveAspectRatio={useMeetLayout ? 'xMidYMid meet' : 'none'}
        className={`asset-trend-chart-svg${useMeetLayout ? ' asset-trend-chart-svg--meet' : ''}${showYAxis ? ' asset-trend-chart-svg--yaxis' : ''}`}
        style={{ height: `${height}px` }}
        onClick={handlePointer}
        onMouseMove={onActiveIndexChange ? handlePointer : undefined}
        onTouchMove={onActiveIndexChange ? handlePointer : undefined}
        onTouchEnd={handlePointer}
        role="presentation"
      >
        <defs>
          <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(96,165,250,0.35)" />
            <stop offset="100%" stopColor="rgba(96,165,250,0.02)" />
          </linearGradient>
        </defs>
        {showYAxis && chart.yTicks.map((tick) => (
          <g key={`yt-${tick.value}`}>
            <line
              x1={chart.left}
              y1={tick.y}
              x2={chart.right}
              y2={tick.y}
              stroke="rgba(148, 163, 184, 0.22)"
              strokeWidth="1"
            />
            <text
              x={chart.left - 6}
              y={tick.y}
              textAnchor="end"
              dominantBaseline="middle"
              className="asset-trend-chart-y-label"
            >
              {formatAxisValue(tick.value)}
            </text>
          </g>
        ))}
        {chart.zeroY != null && (
          <line
            x1={chart.left}
            y1={chart.zeroY}
            x2={chart.right}
            y2={chart.zeroY}
            stroke="rgba(100, 116, 139, 0.45)"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        )}
        <rect
          x={chart.left}
          y={chart.top}
          width={chart.right - chart.left}
          height={chart.bottom - chart.top}
          fill="transparent"
          style={{ cursor: onActiveIndexChange ? 'crosshair' : 'default' }}
        />
        <line
          x1={chart.left}
          y1={chart.bottom}
          x2={chart.right}
          y2={chart.bottom}
          stroke="rgba(148, 163, 184, 0.45)"
          strokeWidth="1"
        />
        <path d={chart.area} fill={`url(#${fillGradientId})`} />
        <path d={chart.line} fill="none" stroke="#60a5fa" strokeWidth="2.5" />
        {onActiveIndexChange && (
          <line
            x1={activePoint.x}
            y1={chart.top}
            x2={activePoint.x}
            y2={chart.bottom}
            stroke="rgba(37, 99, 235, 0.35)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
        )}
        <circle
          cx={activePoint.x}
          cy={activePoint.y}
          r="5"
          fill="#2563eb"
          stroke="#fff"
          strokeWidth="2"
          style={{ pointerEvents: 'none' }}
        />
      </svg>
      <div
        className={`asset-trend-chart-axis${showYAxis ? ' asset-trend-chart-axis--with-y' : ''}`}
        style={showYAxis ? { paddingLeft: xAxisPadLeft, paddingRight: xAxisPadRight } : undefined}
        aria-hidden="true"
      >
        {chart.axisLabels.map((label, index) => (
          <span
            key={`${label.idx}-${label.text}`}
            className={`asset-trend-chart-axis-label ${
              index === 0
                ? 'asset-trend-chart-axis-label--start'
                : (index === chart.axisLabels.length - 1
                  ? 'asset-trend-chart-axis-label--end'
                  : 'asset-trend-chart-axis-label--mid')
            }`}
          >
            {label.text}
          </span>
        ))}
      </div>
    </>
  );

  return (
    <div className={`asset-trend-chart ${onActiveIndexChange ? 'asset-trend-chart--interactive' : ''}${useMeetLayout ? ' asset-trend-chart--meet' : ''}${showYAxis ? ' asset-trend-chart--yaxis' : ''}`}>
      {legendOnTop && legendNode}
      {useMeetLayout ? (
        <div className="asset-trend-chart-frame">{chartBody}</div>
      ) : chartBody}
      {!legendOnTop && legendNode}
    </div>
  );
}
